<template>
  <div class="fixed z-10 inset-0 overflow-y-auto">
    <div class="flex items-center py-8 justify-center min-h-screen">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-black opacity-50"></div>
      </div>
      <div class="bg-white rounded-lg overflow-hidden shadow-xl z-50 px-6 py-6 min-w-[95vw] md:min-w-[90vw] lg:min-w-[1100px] max-w-[95vw]">
        <div class="flex justify-between items-center mb-6 gap-4">
          <div>
            <h2 class="text-xl font-medium text-gray-800">Detalle de compra externa</h2>
            <p class="text-sm text-gray-500 mt-1">Consulta informativa del documento recibido. Esta pantalla no agrega productos al inventario.</p>
          </div>
          <button
            @click="isActive"
            class="text-gray-500 hover:text-gray-700 w-8 h-8 flex items-center justify-center hover:bg-gray-200 rounded-lg"
          >
            <svg class="w-5 h-5">
              <use href="@/assets/svg/icon.svg#cancel" />
            </svg>
          </button>
        </div>

        <div v-if="loading" class="py-10 text-center text-gray-500">Cargando detalle...</div>

        <div v-else>
          <div class="grid md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
            <div class="border rounded-lg px-4 py-3 bg-slate-50">
              <div class="text-xs uppercase text-slate-500 font-semibold">Proveedor</div>
              <div class="text-sm text-slate-800 font-semibold mt-1">{{ register.razon_social_emisor }}</div>
              <div class="text-xs text-slate-500 mt-1">RNC: {{ register.rnc_emisor || '---' }}</div>
            </div>
            <div class="border rounded-lg px-4 py-3 bg-slate-50">
              <div class="text-xs uppercase text-slate-500 font-semibold">Receptor</div>
              <div class="text-sm text-slate-800 font-semibold mt-1">{{ register.razon_social_receptor || '---' }}</div>
              <div class="text-xs text-slate-500 mt-1">RNC: {{ register.rnc_receptor || '---' }}</div>
            </div>
            <div class="border rounded-lg px-4 py-3 bg-slate-50">
              <div class="text-xs uppercase text-slate-500 font-semibold">e-NCF</div>
              <div class="text-sm text-blue-600 font-semibold mt-1">{{ register.ecf }}</div>
              <div class="text-xs text-slate-500 mt-1">Fecha: {{ formatDateTime(register.fecha_emision || register.created_at) }}</div>
            </div>
            <div class="border rounded-lg px-4 py-3 bg-slate-50">
              <div class="text-xs uppercase text-slate-500 font-semibold">Totales</div>
              <div class="text-sm text-slate-800 mt-1">Subtotal: {{ money() }} {{ format(register.subtotal) }}</div>
              <div class="text-sm text-slate-800">Impuesto: {{ money() }} {{ format(register.impuesto_total) }}</div>
              <div class="text-sm text-slate-900 font-bold">Total: {{ money() }} {{ format(register.total) }}</div>
            </div>
          </div>

          <div class="grid md:grid-cols-3 gap-4 mb-6">
            <div class="border rounded-lg px-4 py-3 bg-white">
              <div class="text-xs uppercase text-slate-500 font-semibold">Código de seguridad</div>
              <div class="text-sm text-slate-700 mt-1 break-all">{{ register.codigo_seguridad || '---' }}</div>
            </div>
            <div class="border rounded-lg px-4 py-3 bg-white">
              <div class="text-xs uppercase text-slate-500 font-semibold">Moneda</div>
              <div class="text-sm text-slate-700 mt-1">{{ register.moneda_descripcion || register.moneda_codigo || '---' }}</div>
            </div>
            <div class="border rounded-lg px-4 py-3 bg-white">
              <div class="text-xs uppercase text-slate-500 font-semibold">QR fiscal</div>
              <a v-if="register.qr_url" :href="register.qr_url" target="_blank" rel="noreferrer" class="text-sm text-blue-600 mt-1 break-all block">
                {{ register.qr_url }}
              </a>
              <div v-else class="text-sm text-slate-700 mt-1">---</div>
            </div>
          </div>

          <div class="rounded-lg overflow-hidden border">
            <div class="overflow-x-auto">
              <table class="table-auto w-full">
                <TableHead :headers="header" />
                <tbody class="text-gray-500 text-left border-t-[1px]">
                  <tr
                    v-for="(detail, index) in register.details"
                    :key="index"
                    class="odd:bg-white even:bg-slate-50 font-semibold"
                  >
                    <td class="border-slate-200 text-sm px-6 py-2 whitespace-nowrap">{{ detail.linea }}</td>
                    <td class="border-slate-200 text-sm px-6 py-2 whitespace-nowrap">{{ detail.codigo_externo || '---' }}</td>
                    <td class="border-slate-200 text-sm px-6 py-2 min-w-[280px]">{{ detail.descripcion }}</td>
                    <td class="border-slate-200 text-sm px-6 py-2 whitespace-nowrap">{{ detail.unidad_medida || '---' }}</td>
                    <td class="border-slate-200 text-sm px-6 py-2 whitespace-nowrap">{{ detail.cantidad }}</td>
                    <td class="border-slate-200 text-sm px-6 py-2 whitespace-nowrap">{{ money() }} {{ format(detail.precio_unitario) }}</td>
                    <td class="border-slate-200 text-sm px-6 py-2 whitespace-nowrap">{{ money() }} {{ format(detail.impuesto) }}</td>
                    <td class="border-slate-200 text-sm px-6 py-2 whitespace-nowrap font-bold">{{ money() }} {{ format(detail.total) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="flex justify-end gap-3 mt-6">
            <button
              class="bg-blue-500 text-white px-4 py-2 font-semibold text-sm rounded-lg hover:shadow-sm hover:shadow-blue-600 active:scale-95 flex items-center"
              @click="printOnA4(id)"
            >
              <font-awesome-icon icon="fa-solid fa-print" class="mr-2" />
              Imprimir
            </button>
            <button
              class="bg-slate-100 text-slate-700 px-4 py-2 font-semibold text-sm rounded-lg hover:bg-slate-200 active:scale-95"
              @click="isActive"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, ref } from "vue";
import axios from "@/api";
import TableHead from "@/components/TablesComponents/TableHead";
import { errorActions } from "@/alerts";
import { money, format } from "@/publicjs/money";
import { formatDateTime } from "@/publicjs/formatDate";
import { printOnA4 } from "@/components/Shopping/External/PrintVoucher/PrintOnA4";

export default {
  name: "ExternalShoppingDetailModal",
  props: {
    activeModal: {
      type: Function,
      required: true,
    },
    id: {
      type: Number,
      required: true,
    },
  },
  components: {
    TableHead,
  },
  setup(props) {
    const loading = ref(true);
    const register = ref({
      details: [],
    });
    const header = [
      [
        { title: "Línea" },
        { title: "Cod. externo" },
        { title: "Descripción" },
        { title: "U.M." },
        { title: "Cantidad" },
        { title: "P. unitario" },
        { title: "Impuesto" },
        { title: "Total" },
      ],
    ];

    const isActive = () => {
      props.activeModal(true);
    };

    const getData = async () => {
      loading.value = true;
      const response = await axios.get(`compras-externas/${props.id}`).catch((error) => errorActions(error));

      if (!response) {
        loading.value = false;
        return;
      }

      register.value = response.data;
      loading.value = false;
    };

    onMounted(async () => {
      await getData();
    });

    return {
      loading,
      register,
      header,
      isActive,
      money,
      format,
      formatDateTime,
      printOnA4,
    };
  },
};
</script>